import { useRef, useState } from "react";
import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";
import { parseListItems } from "../lib/list-items";
import {
  badgeClassIn,
  buildBadgeColorMap,
  type BadgeColorEntry,
  type BadgeColorMap,
} from "../lib/value-badge";

type MatrixEntriesEditorProps = {
  value: string;
  onChange: (next: string) => void;
  draft: string;
  onDraftChange: (next: string) => void;
  type: "badge" | "list";
  label: string;
  resetKey: string;
  badgeColors?: BadgeColorMap;
  readOnly: boolean;
};

/**
 * Edits a badge or list cell as one row per entry rather than as a string the
 * user has to punctuate. Several entries can be written at once and split on
 * adding; the value handed back stays the comma-separated string.
 */
export function MatrixEntriesEditor({
  value,
  onChange,
  draft,
  onDraftChange,
  type,
  label,
  resetKey,
  badgeColors,
  readOnly,
}: MatrixEntriesEditorProps) {
  const [entries, setEntries] = useState(() => parseListItems(value));
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const rowRefs = useRef<(HTMLInputElement | null)[]>([]);
  const draftRef = useRef<HTMLInputElement>(null);

  if (lastResetKey !== resetKey) {
    setLastResetKey(resetKey);
    setEntries(parseListItems(value));
    setDuplicate(null);
  }

  function commit(next: string[]) {
    if (readOnly) return;
    setEntries(next);
    onChange(next.filter(Boolean).join(", "));
  }

  function has(candidate: string, ignoreIndex = -1) {
    const key = candidate.trim().toLowerCase();
    return entries.some(
      (entry, index) => index !== ignoreIndex && entry.toLowerCase() === key,
    );
  }

  function addFromText(text: string) {
    if (readOnly) return;
    const parsed = parseListItems(text);
    if (parsed.length === 0) return;

    const added: string[] = [];
    for (const item of parsed) {
      if (
        has(item) ||
        added.some((a) => a.toLowerCase() === item.toLowerCase())
      )
        continue;
      added.push(item);
    }

    if (added.length === 0) {
      setDuplicate(parsed[0] ?? null);
      return;
    }

    setDuplicate(null);
    commit([...entries, ...added]);
    onDraftChange("");
    requestAnimationFrame(() => {
      rowRefs.current[entries.length + added.length - 1]?.scrollIntoView({
        block: "nearest",
      });
    });
  }

  function updateEntry(index: number, text: string) {
    if (readOnly) return;
    if (text.includes(",")) {
      const parts = parseListItems(text);
      const next = [...entries];
      next.splice(index, 1, ...parts);
      commit(next);
      requestAnimationFrame(() =>
        rowRefs.current[index + parts.length - 1]?.focus(),
      );
      return;
    }
    const next = [...entries];
    next[index] = text;
    commit(next);
  }

  function removeEntry(index: number) {
    if (readOnly) return;
    commit(entries.filter((_, i) => i !== index));
    requestAnimationFrame(() => {
      const target = rowRefs.current[index - 1] ?? draftRef.current;
      target?.focus();
    });
  }

  const isBadge = type === "badge";
  const entryNoun = isBadge ? "label" : "item";

  // Split on every keystroke, so the note answers what is typed, not a rejected add.
  const fresh: string[] = [];
  const taken: string[] = [];
  const seenInDraft = new Set<string>();
  for (const item of parseListItems(draft)) {
    const key = item.toLowerCase();
    if (seenInDraft.has(key)) continue;
    seenInDraft.add(key);
    if (has(item)) taken.push(item);
    else fresh.push(item);
  }

  const canAdd = fresh.length > 0;
  const named = taken.length > 0 ? taken : duplicate ? [duplicate] : [];
  const takenNote =
    named.length === 0
      ? null
      : `${named.map((value) => `"${value}"`).join(", ")} ${
          named.length === 1 ? "is" : "are"
        } already in this cell.`;

  const suggestions: BadgeColorEntry[] =
    isBadge && badgeColors ? [...badgeColors.values()] : [];

  const liveColors = isBadge
    ? buildBadgeColorMap([
        ...(badgeColors ? [...badgeColors.values()].map((e) => e.display) : []),
        ...entries,
      ])
    : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-1.5 pb-3">
        <div className="relative">
          <button
            type="button"
            disabled={readOnly || !canAdd}
            onClick={() => {
              addFromText(draft);
              draftRef.current?.focus();
            }}
            aria-label={
              fresh.length > 1
                ? `Add ${fresh.length} ${entryNoun}s`
                : `Add ${fresh[0] ?? entryNoun}`
            }
            className={cn(
              "focus-visible:ring-ring/50 absolute top-1/2 left-1 flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition-colors focus-visible:ring-[3px] focus-visible:outline-none sm:size-7",
              canAdd
                ? "text-primary hover:bg-primary/15 cursor-pointer"
                : // Inert, and out of the way so the click lands in the field.
                  "text-muted-foreground/60 pointer-events-none",
            )}
          >
            <PlusIcon className="size-4" />
          </button>
          <Input
            id="matrix-entry-draft"
            ref={draftRef}
            value={draft}
            disabled={readOnly}
            onChange={(event) => {
              if (readOnly) return;
              setDuplicate(null);
              onDraftChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addFromText(draft);
              }
            }}
            placeholder={`Add ${entryNoun}...`}
            aria-label={`Add ${entryNoun} to ${label}`}
            className="h-11 rounded-full pl-10 text-sm sm:h-9"
          />
        </div>
        {takenNote ? (
          <p role="status" className="text-muted-foreground text-xs">
            {takenNote}
          </p>
        ) : null}
      </div>

      <div aria-hidden className="-mx-4 border-t" />

      <div className="min-h-0 flex-1 overflow-y-auto px-1 py-3 pr-2.5">
        <div className="flex flex-col gap-2">
          {entries.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                aria-hidden
                className="text-muted-foreground flex w-4 shrink-0 items-center justify-end text-xs tabular-nums"
              >
                {isBadge ? (
                  <span className="bg-muted-foreground/60 size-1.5 rounded-full" />
                ) : (
                  index + 1
                )}
              </span>
              <div className="relative min-w-0 flex-1">
                <Input
                  ref={(node) => {
                    rowRefs.current[index] = node;
                  }}
                  value={entry}
                  disabled={readOnly}
                  onChange={(event) => updateEntry(index, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      draftRef.current?.focus();
                    } else if (event.key === "Backspace" && !entry) {
                      event.preventDefault();
                      removeEntry(index);
                    }
                  }}
                  aria-label={`${label} ${entryNoun} ${index + 1}`}
                  className={cn(
                    "h-11 w-full rounded-full pr-10 text-sm sm:h-9",
                    isBadge
                      ? entry &&
                          cn("font-medium", badgeClassIn(liveColors, entry))
                      : // The surface the grid chips use, so both look the same.
                        "bg-foreground/8 dark:bg-foreground/8 border-transparent",
                  )}
                />
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => removeEntry(index)}
                  aria-label={`Remove ${entry || `${entryNoun} ${index + 1}`}`}
                  title="Remove"
                  className="text-muted-foreground hover:bg-destructive/15 hover:text-destructive focus-visible:ring-ring/50 absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition-colors focus-visible:ring-[3px] focus-visible:outline-none sm:size-7"
                >
                  <XIcon className="size-3.5" />
                </button>
              </div>
            </div>
          ))}

          {entries.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No {entryNoun}s yet.
            </p>
          ) : null}
        </div>
      </div>

      {suggestions.length > 0 ? (
        <>
          <div aria-hidden className="-mx-4 border-t" />
          <div className="pt-3">
            <p className="text-muted-foreground mb-2.5 text-xs font-medium">
              Used in this matrix:
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((entry) => {
                const present = has(entry.display);
                return (
                  <button
                    key={entry.display}
                    type="button"
                    aria-pressed={present}
                    disabled={readOnly}
                    onClick={() =>
                      present
                        ? setDuplicate(entry.display)
                        : addFromText(entry.display)
                    }
                    title={
                      present ? "Already in this cell" : `Add ${entry.display}`
                    }
                    className={cn(
                      "focus-visible:ring-ring/50 cursor-pointer rounded-full transition-opacity focus-visible:ring-[3px] focus-visible:outline-none",
                      present ? "opacity-40" : "hover:opacity-70",
                    )}
                  >
                    <Badge className={cn("gap-1", entry.className)}>
                      {present ? (
                        <CheckIcon className="size-3" aria-hidden />
                      ) : null}
                      {entry.display}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
